import { GuidService } from 'ui-components/services/guid-service';

import { createAdjustItemsRequestItem } from '../../../../../../orders/split-ship/helpers/create-adjust-items-request-item';
import { generateSplitShipEntityChecksum } from '../../../../../../orders/split-ship/helpers/generate-split-ship-entity-checksum';

/**
 * @typedef {import('../../../../../../orders/split-ship/split-ship-api-types.d.ts').AdjustItemsRequest} AdjustItemsRequest
 */

/**
 * @typedef {import('../../../asn-types.d.ts').AsnBox} AsnBox
 */

// Split ship requires an adjust items request for both the source and destination fulfillment plans. Since all split items are coming from
// the original fulfillment plan, we need to create adjust items requests for all the items moving out of it.
const createSplitShipItemsForInitialFulfillmentPlan = (originalFulfillmentPlanId, boxProducts, originalFulfillmentPlanItems) => {
  return originalFulfillmentPlanItems.reduce((acc, originalFulfillmentPlanItem) => {
    const asnProduct = boxProducts.find((p) => p.sku === originalFulfillmentPlanItem.sku);
    const asnProductQuantityInBox = asnProduct ? (asnProduct.quantityPerBox ?? asnProduct.quantity) : 0;

    const shouldSplitItem = asnProductQuantityInBox !== originalFulfillmentPlanItem.quantity;
    if (shouldSplitItem) {
      acc.push(
        createAdjustItemsRequestItem(originalFulfillmentPlanId, originalFulfillmentPlanItem.fulfillmentPlanItemId, asnProductQuantityInBox),
      );
    }

    return acc;
  }, []);
};

// Newly created fulfillment plans will not have any items moving out of them, only into them. This means we can just map the ASN products
// directly to adjust items requests.
const createSplitShipItemsForNewFulfillmentPlan = (originalFulfillmentPlanId, boxProducts, fulfillmentPlanItemsBySku) => {
  return boxProducts.map((asnProduct) => {
    const quantity = asnProduct.quantityPerBox ?? asnProduct.quantity;
    const { fulfillmentPlanItemId } = fulfillmentPlanItemsBySku[asnProduct.sku];
    return createAdjustItemsRequestItem(originalFulfillmentPlanId, fulfillmentPlanItemId, quantity);
  });
};

/**
 * Creates the split request to divide the single order into multiple fulfillment plans
 * based on the ASN boxes configuration
 * @param {Array<AsnBox>} boxes - Array of box configurations
 * @param {string} salesOrderId
 * @param {object} originalFulfillmentPlan
 * @returns {AdjustItemsRequest} Request object for adjustItemsSync API
 */
export const createFulfillmentPlanSplitRequest = (boxes, salesOrderId, originalFulfillmentPlan) => {
  const originalFulfillmentPlanId = originalFulfillmentPlan.fulfillmentPlanId;

  // Create a map of SKU to fulfillment plan item for easy lookup
  const fulfillmentPlanItemsBySku = originalFulfillmentPlan.items.reduce(
    (acc, item) => ({
      ...acc,
      [item.sku]: {
        fulfillmentPlanId: originalFulfillmentPlanId,
        fulfillmentPlanItemId: item.fulfillmentPlanItemId,
        quantity: item.quantity,
      },
    }),
    {},
  );

  const fulfillmentPlans = boxes.reduce((acc, box, boxIndex) => {
    const boxCount = box.boxesPerConfig || 1;

    for (let i = 0; i < boxCount; i++) {
      const isFirstBox = boxIndex === 0 && i === 0;

      const items = isFirstBox
        ? createSplitShipItemsForInitialFulfillmentPlan(originalFulfillmentPlanId, box.products, originalFulfillmentPlan.items)
        : createSplitShipItemsForNewFulfillmentPlan(originalFulfillmentPlanId, box.products, fulfillmentPlanItemsBySku);

      acc.push({
        fulfillmentPlanId: isFirstBox ? originalFulfillmentPlanId : null,
        items,
        requestId: GuidService.newGuid(),
      });
    }

    return acc;
  }, []);

  const initialSplitShipEntities = Object.values(fulfillmentPlanItemsBySku);
  const entityChecksum = generateSplitShipEntityChecksum(initialSplitShipEntities);

  return {
    fulfillmentPlans,
    salesOrderId,
    entityChecksum,
  };
};
